import React, { useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { isScrollingDisabled, manageDisableScrolling } from '../../ducks/ui.duck';
import { IconArrowHead, Modal, Page } from '../../components';
import DhobaraSiteNav from '../../components/DhobaraSiteNav/DhobaraSiteNav';
import DhobaraFooter from '../../components/DhobaraFooter/DhobaraFooter';
import { storeData } from '../CheckoutPage/CheckoutPageSessionHelpers';
import { setInitialValues } from '../CheckoutPage/CheckoutPage.duck';
import { createSlug } from '../../util/urlHelpers';
import { addToCart, isInCart } from '../../util/cart';
import { toggleSavedListing } from '../CartPage/CartPage.duck';
import { getListingLabelMap } from '../../config/configListing';
import { isSoldOut } from '../../util/soldOut';

import css from './DhobaraListingPage.module.css';

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatPrice(priceObj) {
  if (!priceObj) return null;
  const amount = priceObj.amount / 100;
  return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function getImageUrl(image, variant = 'scaled-medium') {
  const variants = image?.attributes?.variants || {};
  return (
    variants[variant]?.url ||
    variants['scaled-large']?.url ||
    variants['listing-card-2x']?.url ||
    variants['listing-card']?.url ||
    null
  );
}

function getInitials(name) {
  if (!name) return '?';
  return name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
}

// Maps Dhobara's seller-facing condition values to schema.org's ItemCondition
// enum so search engines/AI shopping agents can parse it (Product schema, AEO/GEO).
const CONDITION_SCHEMA_MAP = {
  'new-with-tags': 'https://schema.org/NewCondition',
  'like-new': 'https://schema.org/NewCondition',
  excellent: 'https://schema.org/UsedCondition',
  good: 'https://schema.org/UsedCondition',
  fair: 'https://schema.org/UsedCondition',
};

// Builds schema.org Product JSON-LD for a listing so the PDP is eligible for
// rich shopping results and can be parsed/quoted directly by AI answer engines.
function buildProductSchema(listing, slug, id) {
  if (!listing) return null;
  const attrs = listing.attributes || {};
  const pub = attrs.publicData || {};
  const price = attrs.price;
  const images = listing.images || [];
  const designer = pub.designer || '';
  const garmentTypes = Array.isArray(pub.garmentType)
    ? pub.garmentType
    : pub.garmentType
    ? [pub.garmentType]
    : [];
  const condition = pub.condition || '';
  const title = attrs.title || garmentTypes.join(', ') || 'Garment';
  const description =
    attrs.description ||
    `${designer ? designer + ' ' : ''}${title} — pre-owned, authenticated South Asian designer wear on Dhobara.`;
  const imageUrls = images.map(img => getImageUrl(img, 'scaled-large')).filter(Boolean);
  const pdpUrl = slug && id ? `https://dhobara.com/l/${slug}/${id}` : null;

  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: designer ? `${designer} ${title}` : title,
    description,
    ...(pdpUrl ? { url: pdpUrl } : {}),
    ...(imageUrls.length > 0 ? { image: imageUrls } : {}),
    ...(designer ? { brand: { '@type': 'Brand', name: designer } } : {}),
    ...(condition
      ? { itemCondition: CONDITION_SCHEMA_MAP[condition] || 'https://schema.org/UsedCondition' }
      : {}),
    offers: {
      '@type': 'Offer',
      priceCurrency: price?.currency || 'USD',
      ...(price ? { price: (price.amount / 100).toFixed(2) } : {}),
      ...(pdpUrl ? { url: pdpUrl } : {}),
      availability: 'https://schema.org/InStock',
      seller: {
        '@type': 'Organization',
        name: 'Dhobara',
        url: 'https://dhobara.com',
      },
    },
  };
}

// ── Accordion item ────────────────────────────────────────────────────────────

function AccordionItem({ label, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={css.accordionItem}>
      <button className={css.accordionBtn} onClick={() => setOpen(o => !o)} type="button">
        <span>{label}</span>
        <span className={`${css.accordionChevron} ${open ? css.accordionChevronOpen : ''}`}>▾</span>
      </button>
      {open && <div className={css.accordionContent}>{children}</div>}
    </div>
  );
}

// ── Main listing detail ───────────────────────────────────────────────────────

function ListingDetail({ listing }) {
  const [activeIdx, setActiveIdx] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const history = useHistory();
  const dispatch = useDispatch();

  const onManageDisableScrolling = useCallback(
    (componentId, disableScrolling) =>
      dispatch(manageDisableScrolling(componentId, disableScrolling)),
    [dispatch]
  );

  const isAuthenticated = useSelector(state => state.auth?.isAuthenticated);
  const currentUser = useSelector(state => state.user?.currentUser);

  const attrs = listing.attributes || {};
  const pub = attrs.publicData || {};
  const price = attrs.price;
  const images = listing.images || [];
  const author = listing.author;
  const authorProfile = author?.attributes?.profile || {};
  const authorDisplayName = authorProfile.displayName || authorProfile.firstName || 'Seller';
  const authorBio = authorProfile.bio || '';
  const authorAvatar = author?.profileImage?.attributes?.variants?.['square-small']?.url || null;

  // Field values
  const designer = pub.designer || '';
  const garmentTypes = Array.isArray(pub.garmentType) ? pub.garmentType : (pub.garmentType ? [pub.garmentType] : []);
  const condition = pub.condition || '';
  const size = pub.size || '';
  const color = pub.color || '';
  const fabric = pub.fabric || '';
  const measurements = pub.measurements || '';
  // Dhobara: the buyer-facing badge reflects the Claude Vision authenticity
  // check run on Step 2 save (aiAuthenticityVerified), not the seller's own
  // "Do you have proof of authenticity?" self-report (pub.authenticity) -
  // that field still feeds the search filter, but the PDP badge is now
  // AI-verified rather than self-declared.
  const authenticity = pub.aiAuthenticityVerified;
  const collection = pub.collection || '';

  const title = attrs.title || garmentTypes.join(', ') || 'Garment';
  const description = attrs.description || '';
  const priceStr = formatPrice(price);

  // Checkout navigation
  const listingId = listing.id?.uuid;
  const listingSlug = createSlug(attrs.title || '');

  const [saved, setSaved] = useState(false);

  // Sync saved state: server (privateData) when logged in, localStorage otherwise
  useEffect(() => {
    if (!listingId) return;
    if (isAuthenticated && currentUser) {
      const serverIds =
        currentUser.attributes?.profile?.privateData?.savedListings || [];
      setSaved(serverIds.includes(listingId));
    } else {
      setSaved(isInCart(listingId));
    }
  }, [listingId, isAuthenticated, currentUser]);

  const handleBuyNow = useCallback(() => {
    if (!listingId) return;
    const orderData = { deliveryMethod: 'shipping', quantity: 1 };
    // Dispatch to Redux so data survives the login redirect (Redux isn't reset on login)
    dispatch(setInitialValues({ listing, orderData, confirmPaymentError: null }));
    // Also persist to sessionStorage as fallback for page refresh
    storeData(orderData, listing, null, 'CheckoutPage');
    history.push(`/l/${listingSlug}/${listingId}/checkout`);
  }, [listingId, listingSlug, listing, history, dispatch]);

  const handleSaveItem = useCallback(() => {
    if (!listingId) return;
    if (isAuthenticated) {
      // Toggle in Sharetribe privateData (syncs across devices)
      dispatch(toggleSavedListing(listingId)).then(nowSaved => {
        if (nowSaved !== null) setSaved(nowSaved);
      });
    } else {
      // Guest: persist to localStorage
      const firstImage = listing.images?.[0];
      const imageUrl =
        firstImage?.attributes?.variants?.['listing-card-2x']?.url ||
        firstImage?.attributes?.variants?.['listing-card']?.url ||
        firstImage?.attributes?.variants?.['scaled-medium']?.url ||
        null;
      addToCart({
        listingId,
        slug: listingSlug,
        title: attrs.title || '',
        designer: pub.designer || '',
        priceAmount: price?.amount || null,
        currency: price?.currency || 'USD',
        imageUrl,
      });
      setSaved(true);
    }
  }, [listingId, listingSlug, listing, attrs, pub, price, isAuthenticated, dispatch]);

  // Canonicalize condition label
  const conditionMap = {
    'new-with-tags': 'New with Tags', 'like-new': 'Like New', 'excellent': 'Excellent', 'good': 'Good', 'fair': 'Fair',
  };
  const conditionLabel = conditionMap[condition] || condition;

  // Size label
  const sizeMap = {
    'xs': 'XS', 's': 'S', 'm': 'M', 'l': 'L', 'xl': 'XL', 'made-to-measure': 'Made-to-Measure',
  };
  const sizeLabel = sizeMap[size] || size;

  // Colour had no map at all, so buyers saw the raw option key —
  // "purple-plum-mauve" instead of "Purple / Plum / Mauve" — on every listing
  // that has one. Read the labels from configListing rather than hand-copying
  // them, the way conditionMap and sizeMap above do; those two duplicate the
  // config and will drift the moment an option is renamed.
  // A sold listing stays published with zero stock and is still reachable —
  // browse pages now show it marked SOLD, and old links keep working — so the
  // page has to refuse the sale rather than rely on it being unreachable.
  const sold = isSoldOut(listing);

  const colorLabels = getListingLabelMap('color');
  const colorLabel = colorLabels[color] || color;

  // Dhobara: the inline gallery deliberately crops photos to a fixed 3:4
  // grid (see .mainImage/.thumb - object-fit: cover) for a tidy layout, but
  // that means a buyer can never see the FULL garment for photos with a
  // different aspect ratio. Clicking the photo opens a lightbox with the
  // full, uncropped image (object-fit: contain) at a larger size instead.
  const openLightbox = useCallback(() => {
    if (images.length > 0) setIsLightboxOpen(true);
  }, [images.length]);
  const closeLightbox = useCallback(() => setIsLightboxOpen(false), []);
  const showPrevImage = useCallback(() => {
    setActiveIdx(i => (i - 1 + images.length) % images.length);
  }, [images.length]);
  const showNextImage = useCallback(() => {
    setActiveIdx(i => (i + 1) % images.length);
  }, [images.length]);

  // Left/right arrow keys navigate while the lightbox is open. Escape-to-
  // close is already handled by the shared Modal component.
  useEffect(() => {
    if (!isLightboxOpen || images.length <= 1) return undefined;
    const handleKeyDown = e => {
      if (e.key === 'ArrowLeft') showPrevImage();
      else if (e.key === 'ArrowRight') showNextImage();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isLightboxOpen, images.length, showPrevImage, showNextImage]);

  return (
    <div>
      <div className={css.grid}>
        {/* ── Left: gallery ─────────────────────────────────────────── */}
        <div>
          <div
            className={css.mainImage}
            onClick={openLightbox}
            role={images.length > 0 ? 'button' : undefined}
            tabIndex={images.length > 0 ? 0 : undefined}
            onKeyDown={e => {
              if (images.length > 0 && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                openLightbox();
              }
            }}
          >
            {images[activeIdx] && (
              <img
                src={getImageUrl(images[activeIdx], 'scaled-large')}
                alt={`${designer} ${title}`}
              />
            )}
            {!images[activeIdx] && (
              <div style={{ width: '100%', height: '100%', background: '#ede8dc' }} />
            )}
          </div>
          {images.length > 1 && (
            <div className={css.thumbnails}>
              {images.slice(0, 8).map((img, i) => (
                <button
                  key={i}
                  type="button"
                  className={`${css.thumb} ${i === activeIdx ? css.thumbActive : ''}`}
                  onClick={() => setActiveIdx(i)}
                >
                  <img src={getImageUrl(img, 'listing-card')} alt="" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* ── Right: details ────────────────────────────────────────── */}
        <div>
          {designer && <p className={css.designerLabel}>{designer}</p>}
          <h1 className={css.title}>{title}</h1>

          {/* Badges */}
          <div className={css.badges}>
            {collection && <span className={css.badge}>{collection}</span>}
            {conditionLabel && <span className={css.badge}>{conditionLabel}</span>}
            {sizeLabel && <span className={css.badge}>Size, {sizeLabel}</span>}
            {authenticity && (
              <span className={`${css.badge} ${css.badgeAccent}`}>Authentic Designer Tags</span>
            )}
          </div>

          {/* Price */}
          {priceStr && (
            <div className={css.priceRow}>
              <p className={css.price}>{priceStr}</p>
            </div>
          )}

          {/* Description */}
          {description && <p className={css.description}>{description}</p>}

          {/* Color */}
          {color && (
            <div className={css.detailRow}>
              <p className={css.detailLabel}>Color</p>
              <p className={css.detailValue}>{colorLabel}</p>
            </div>
          )}

          {/* CTA */}
          <div className={css.ctaRow}>
            <button
              className={css.btnBuy}
              type="button"
              disabled={!listingId || sold}
              onClick={handleBuyNow}
            >
              {sold ? 'Sold' : 'Buy Now'}
            </button>
            <button
              className={saved ? `${css.btnBag} ${css.btnBagSaved}` : css.btnBag}
              type="button"
              onClick={handleSaveItem}
            >
              {saved ? '✓ Saved to Items' : 'Save This Item'}
            </button>
          </div>
          <p className={css.buyerNote}>Secure checkout · Buyer Protection on every order</p>

          {/* Accordion */}
          <div className={css.accordionSection}>
            {measurements && (
              <AccordionItem label="Size Guide & Measurements">
                <p style={{ whiteSpace: 'pre-wrap' }}>{measurements}</p>
                <p style={{ fontStyle: 'italic', fontSize: 12, marginTop: 12, color: '#7a7168' }}>
                  Measurements are provided to support made-to-measure adjustments by your tailor.
                </p>
              </AccordionItem>
            )}
            {garmentTypes.length > 0 && (
              <AccordionItem label="What's Included">
                <ul>
                  {garmentTypes.map(g => <li key={g}>{g}</li>)}
                </ul>
              </AccordionItem>
            )}
            {fabric && (
              <AccordionItem label="Fabric & Craft">
                <p>{fabric}</p>
              </AccordionItem>
            )}
          </div>
        </div>
      </div>

      {/* ── Seller section ─────────────────────────────────────────────── */}
      {author && (
        <section className={css.sellerSection}>
          <h2 className={css.sellerHeading}>Meet the Seller</h2>
          <div className={css.sellerCard}>
            <div className={css.sellerAvatar}>
              {authorAvatar
                ? <img src={authorAvatar} alt={authorDisplayName} />
                : getInitials(authorDisplayName)
              }
            </div>
            <div className={css.sellerInfo}>
              <p className={css.sellerName}>{authorDisplayName}</p>
              {authorBio && <p className={css.sellerBio}>{authorBio}</p>}
            </div>
          </div>
        </section>
      )}

      {/* ── Photo lightbox ────────────────────────────────────────────── */}
      <Modal
        id="ListingImageLightbox"
        isOpen={isLightboxOpen}
        onClose={closeLightbox}
        onManageDisableScrolling={onManageDisableScrolling}
        containerClassName={css.lightboxContainer}
      >
        {images.length > 0 && (
          <div className={css.lightbox}>
            <div className={css.lightboxImageWrapper}>
              {images.length > 1 && (
                <button
                  type="button"
                  className={css.lightboxNavLeft}
                  onClick={showPrevImage}
                  aria-label="Previous photo"
                >
                  <IconArrowHead direction="left" size="big" />
                </button>
              )}
              <img
                className={css.lightboxImage}
                src={getImageUrl(images[activeIdx], 'scaled-xlarge')}
                alt={`${designer} ${title}`}
              />
              {images.length > 1 && (
                <button
                  type="button"
                  className={css.lightboxNavRight}
                  onClick={showNextImage}
                  aria-label="Next photo"
                >
                  <IconArrowHead direction="right" size="big" />
                </button>
              )}
            </div>
            {images.length > 1 && (
              <div className={css.lightboxCounter}>
                {activeIdx + 1} / {images.length}
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}

// ── Page component ────────────────────────────────────────────────────────────

const DhobaraListingPage = ({ params }) => {
  const store = useStore();
  const scrollingDisabled = useSelector(state => isScrollingDisabled(state));
  const { showListingError } = useSelector(state => state.ListingPage || {});

  const getListing = useCallback(
    id => {
      const state = store.getState();
      const ref = { id, type: 'listing' };
      const listings = getMarketplaceEntities(state, [ref]);
      return listings.length === 1 ? listings[0] : null;
    },
    [store]
  );

  const getOwnListing = useCallback(
    id => {
      const state = store.getState();
      const ref = { id, type: 'ownListing' };
      const listings = getMarketplaceEntities(state, [ref]);
      return listings.length === 1 ? listings[0] : null;
    },
    [store]
  );

  const listingId = params?.id ? { uuid: params.id } : null;
  const isPendingApprovalVariant = params?.variant === 'pending_approval';
  const isDraftVariant = params?.variant === 'draft';
  const currentListing = isPendingApprovalVariant || isDraftVariant
    ? getOwnListing(listingId)
    : getListing(listingId);

  const listing = currentListing;
  const attrs = listing?.attributes || {};
  const pub = attrs.publicData || {};
  const designer = pub.designer || '';
  const garmentTypes = Array.isArray(pub.garmentType)
    ? pub.garmentType
    : pub.garmentType
    ? [pub.garmentType]
    : [];
  const rawTitle = attrs.title || garmentTypes.join(', ') || 'Listing';
  const pageTitle = listing ? `${designer ? designer + ' — ' : ''}${rawTitle} | Dhobara` : 'Listing | Dhobara';

  const rawDescription = attrs.description || '';
  const metaDescription = rawDescription
    ? rawDescription.length > 155
      ? `${rawDescription.slice(0, 152).trim()}…`
      : rawDescription
    : `Shop ${designer ? designer + ' ' : ''}${rawTitle} — authenticated pre-owned South Asian designer wear on Dhobara.`;

  const listingImages = listing?.images || [];
  const heroImageUrl = listingImages[0] ? getImageUrl(listingImages[0], 'scaled-large') : null;
  const facebookImages = heroImageUrl
    ? [{ name: 'facebook', url: heroImageUrl, width: 1200, height: 630 }]
    : undefined;

  const listingUUID = params?.id || null;
  const listingSlugForSchema = listing ? createSlug(rawTitle) : null;
  const productSchema = buildProductSchema(listing, listingSlugForSchema, listingUUID);

  return (
    <Page
      title={pageTitle}
      description={metaDescription}
      schema={productSchema || undefined}
      facebookImages={facebookImages}
      openGraphType="product"
      scrollingDisabled={scrollingDisabled}
    >
      <div className={css.page}>
        <DhobaraSiteNav />
        <main className={css.main}>
          {showListingError ? (
            <div className={css.errorMsg}>
              <p>This listing is not available.</p>
              <Link to="/s">Browse other listings</Link>
            </div>
          ) : !listing ? (
            <div className={css.loading}>Loading…</div>
          ) : (
            <ListingDetail listing={listing} />
          )}
        </main>
        <DhobaraFooter />
      </div>
    </Page>
  );
};

export default DhobaraListingPage;
